// Components
import { Tab, TabPanel, TabPanels, Tabs, TabsList } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import useQuery from '../../../hooks/useQuery';
import { useSIESConfig } from '../../../hooks/useSIESConfig';
import { useSNIESConfig } from '../../../hooks/useSNIESConfig';
import useUserState from '../../../hooks/useUserState';
import { TranslationsKeys } from '../../../locales/translations';
import { PermissionName } from '../../../types/Auth';

// Types
import ReportAsync from './ReportAsync';
import ReportSIES from './ReportSIES';
import ReportSNIES from './ReportSNIES';
import ReportSync from './ReportSync';

enum TabName {
  ReportsSync = 'ReportsSync',
  ReportsSIES = 'ReportsSIES',
  ReportsSNIES = 'ReportsSNIES',
  ReportsAsync = 'ReportsAsync',
}

type TabRoute = {
  Component: () => JSX.Element;
  name: TabName;
  requiredPermissions: PermissionName[];
  allPermisionsRequired?: boolean;
  configs?: Record<string, any>;
};

// Constants
const TAB_ROUTES: TabRoute[] = [
  {
    Component: () => <ReportSync />,
    name: TabName.ReportsSync,
    requiredPermissions: [PermissionName.REPORTS],
  },
  {
    Component: () => <ReportSIES />,
    name: TabName.ReportsSIES,
    requiredPermissions: [PermissionName.REPORTS],
    configs: { siesEnabled: true },
  },
  {
    Component: () => <ReportSNIES />,
    name: TabName.ReportsSNIES,
    requiredPermissions: [PermissionName.REPORTS],
    configs: { sniesEnabled: true },
  },
  {
    Component: () => <ReportAsync />,
    name: TabName.ReportsAsync,
    requiredPermissions: [PermissionName.REPORTS],
  },
];

const Reports = () => {
  const history = useHistory();
  const { path } = useRouteMatch();
  const params = useQuery();
  const { isAuthorizedTo } = useUserState();

  const { t } = useTranslation(TranslationsKeys.MANAGEMENT_TOOLS_REPORTS_TABS);

  const { siesEnabled } = useSIESConfig();
  const { sniesEnabled } = useSNIESConfig();

  const configs: Record<string, any> = useMemo(
    () => ({ siesEnabled, sniesEnabled }),
    [siesEnabled, sniesEnabled],
  );

  const authorizedRoute = useMemo(() => {
    const permissionFiltered = TAB_ROUTES.filter(
      (route) =>
        !route.requiredPermissions?.length ||
        isAuthorizedTo(route.requiredPermissions, route.allPermisionsRequired),
    );

    return permissionFiltered.filter((e) => {
      if (!e?.configs || !Object.keys(e?.configs).length) {
        return true;
      }

      let valid = true;

      Object.entries(e?.configs).forEach(([key, value]) => {
        if (configs[key] !== value) {
          valid = false;
        }
      });

      return valid;
    });
  }, [isAuthorizedTo, configs]);

  const defaultTab = useMemo(() => {
    if (!authorizedRoute?.length) {
      return TabName.ReportsSync;
    }
    return authorizedRoute[0].name;
  }, [authorizedRoute]);

  const handleTabSelection = (selectedTab: number | string) => {
    history.push({ pathname: path, search: `?tab=${selectedTab}` });
    return null;
  };

  const initialTab = useMemo(() => {
    const queryTab = params.get('tab');
    if (!queryTab) return defaultTab;

    const isValidTab = authorizedRoute.some((ar) => ar.name === queryTab);
    return isValidTab ? queryTab : defaultTab;
  }, [params, defaultTab, authorizedRoute]);

  return (
    <div className="mx-3">
      <Tabs defaultTab={initialTab} onSelection={handleTabSelection}>
        <TabsList>
          {authorizedRoute.map((route) => (
            <Tab id={route.name} key={route.name}>
              {t(`${route.name}`)}
            </Tab>
          ))}
        </TabsList>
        <TabPanels>
          {authorizedRoute.map(({ Component, ...route }) => (
            <TabPanel
              id={route.name}
              key={route.name}
              children={<Component />}
            />
          ))}
        </TabPanels>
      </Tabs>
    </div>
  );
};

export default Reports;
